import { DateTime } from 'luxon';
import { WhenMomentUnreadable, mayHappenAgainSinceStored } from '@cf-shared/periodic-suppression';
import { compareSemver, trimAppVersion } from '@cf-shared/version-compare';
// Type-only import: `Signal` is erased at transpile time, so this does
// NOT pull `@angular/core` into the file at runtime — the dispatch
// helpers stay loadable by mocha's plain-Node ts-node register hook.
import type { Signal } from '@angular/core';

// Pure helpers backing VersionCheckService. Kept free of Angular and
// Capacitor so the platform-decision table and dismissal-storage
// round-trip can be unit-tested without a DOM, mirroring the
// app-install-resolver.ts / app-install.service.ts split.
//
// See design-docs/in-app-update-prompt.md for the architectural
// rationale and the supportsUpdateChecking() decision table.

export type VersionCheckSurface = 'startup' | 'resume' | 'settings';

export type VersionCheckResult =
  | { updateAvailable: false }
  | { updateAvailable: true; availableVersion: string };

// Live state of an Android flexible-update download, as surfaced to
// the home-dashboard card. Only the Android backend ever leaves Idle;
// PWA and iOS hold a constant Idle signal because neither platform has
// a background-download the UI can observe (PWA does skipWaiting +
// reload; iOS hands off to the App Store and leaves the app). The card
// reads this to switch between its three modes:
//   Idle        → "A new version is available." + Update button
//   Downloading → "Downloading update…"          + no button
//   Ready       → "Update downloaded."           + Restart button
export enum UpdateInstallState {
  Idle = 'Idle',
  Downloading = 'Downloading',
  Ready = 'Ready',
}

// Backend contract — implemented by the three per-platform files.
// The service dispatches to one of them based on the platform flags.
//
// `belowMinimum` (#6770) is whether the running build is below
// config/MinimumSupportedVersion. It is passed in by VersionCheckService because a
// backend cannot ask it (that would be a dependency cycle), and is forwarded to the
// Android `completed` telemetry, which every method below can reach.
export interface VersionCheckBackend {
  getStatusAsync(): Promise<VersionCheckResult>;
  startUpdateAsync(belowMinimum: boolean): Promise<void>;
  // The "Please update" alert's Update button. Android: Play's
  // full-screen download when allowed, else the Play Store listing. iOS / PWA: the
  // same as startUpdateAsync.
  startRequiredUpdateAsync(belowMinimum: boolean): Promise<void>;
  // Live install-progress state for the flexible-update flow. The card
  // reads this (a zoneless signal, so the read auto-tracks for change
  // detection) to pick its display mode. PWA/iOS expose a constant
  // Idle signal.
  readonly installState: Signal<UpdateInstallState>;
  // Finishes a downloaded flexible update by restarting the app
  // (Android, called when the user taps Restart on the Ready card).
  // No-op on PWA/iOS, which never reach the Ready state.
  completeUpdateAsync(belowMinimum: boolean): Promise<void>;
}

// All the runtime inputs the dispatcher needs, in one record so the
// pure helpers are trivially testable.
export interface VersionCheckPlatformFlags {
  isCapacitorNative: boolean;
  isPlatformAndroid: boolean;
  isPlatformIOS: boolean;
  hasServiceWorker: boolean;
  hostname: string;
}

export interface VersionCheckBackends {
  android: VersionCheckBackend;
  pwa: VersionCheckBackend;
  ios: VersionCheckBackend;
}

// Minimal Storage-like shape so the dismissal helpers can be tested
// without a real localStorage (mocha runs in plain Node — no DOM).
export interface VersionCheckStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

// Per-version dismissal key prefix. The version is part of the key so
// a new availableVersion is treated as un-dismissed regardless of any
// prior dismissals — see design-docs/in-app-update-prompt.md §Dismiss
// Policy.
export const DISMISS_KEY_PREFIX = 'update_prompt_dismissed:';
export const DISMISS_WINDOW_HOURS = 24;

// Returns the supportsUpdateChecking() answer given platform flags.
// On Capacitor-native, Android is supported (real Play flexible flow)
// and iOS is supported (iTunes Lookup detector — see
// version-check.ios-lookup.ts). On web/PWA: SW must be available AND
// the hostname must not be the local-dev pattern where index.html
// deliberately skips SW registration.
//
// The `hostname.startsWith('local')` check is intentional verbatim
// parity with the existing SW-skip condition in `client/src/index.html`
// (the `location.hostname.startsWith('local')` branch that decides
// whether `navigator.serviceWorker.register('/sw.js')` runs at all).
// Both must move together: if index.html's check ever narrows to
// `=== 'localhost'`, this check must follow, or the dispatcher will
// claim support on a hostname where no SW will ever register.
export function supportsUpdateCheckingFor(flags: VersionCheckPlatformFlags): boolean {
  // On Capacitor-native both Android (Play flexible flow) and iOS (iTunes
  // Lookup detector) are supported; any other native platform is not.
  if (flags.isCapacitorNative) return flags.isPlatformAndroid || flags.isPlatformIOS;
  if (!flags.hasServiceWorker) return false;
  if (flags.hostname.startsWith('local')) return false;
  return true;
}

// Selects which backend to dispatch to. Same platform rules as
// supportsUpdateCheckingFor, but always returns a backend — callers
// gate on supportsUpdateChecking before calling, and the always-return
// shape is defensive for any caller that doesn't.
export function chooseBackend(
  flags: VersionCheckPlatformFlags,
  backends: VersionCheckBackends,
): VersionCheckBackend {
  if (flags.isCapacitorNative) {
    return flags.isPlatformAndroid ? backends.android : backends.ios;
  }
  return backends.pwa;
}

// True if the user dismissed this exact availableVersion within the
// last DISMISS_WINDOW_HOURS.
//
// A missing key and a damaged stored value both read as "not
// dismissed-recently", so the card shows. That is what
// `WhenMomentUnreadable.Allow` says out loud, and it is the right
// direction here: showing the card a day early costs the user one
// dismissal, while holding it back would hide an available update
// forever on a device with a damaged value.
export function isDismissedRecently(
  storage: VersionCheckStorage,
  availableVersion: string,
  now: DateTime,
): boolean {
  const stored = storage.getItem(DISMISS_KEY_PREFIX + availableVersion);
  return !mayHappenAgainSinceStored(
    stored,
    { hours: DISMISS_WINDOW_HOURS },
    now,
    WhenMomentUnreadable.Allow,
  );
}

export function recordDismissal(
  storage: VersionCheckStorage,
  availableVersion: string,
  now: DateTime,
): void {
  // `toISO()!` is safe: callers always pass `DateTime.now()` (a valid
  // DateTime), and Luxon's `toISO()` only returns null on Invalid
  // DateTimes. The tests pass a valid `DateTime.fromISO(...)` which
  // is also non-null. If a future caller passes a possibly-invalid
  // DateTime, this assertion will mask the bug — revisit the
  // signature then (e.g. tighten to a branded `ValidDateTime`).
  storage.setItem(DISMISS_KEY_PREFIX + availableVersion, now.toISO()!);
}

// Per-session per-surface throttle state, extracted so it can be
// unit-tested without importing `@angular/core` (mocha's ts-node
// register hook can't load files that transitively pull in Angular).
// The service holds one instance of this and reads/writes it on
// every getStatusAsync call.
export interface VersionCheckThrottleState {
  queriedSurfaces: Set<VersionCheckSurface>;
  cachedResult: VersionCheckResult | null;
}

export function makeThrottleState(): VersionCheckThrottleState {
  return { queriedSurfaces: new Set(), cachedResult: null };
}

// True iff the throttle is satisfied for this surface and a prior
// result is on hand to return. Startup and resume are throttled
// (each at most one backend hit per session); settings is always
// fresh.
export function shouldServeFromCache(
  state: VersionCheckThrottleState,
  surface: VersionCheckSurface,
): boolean {
  const throttled = surface === 'startup' || surface === 'resume';
  return throttled && state.queriedSurfaces.has(surface) && state.cachedResult !== null;
}

// Records the backend's answer for this surface so subsequent
// throttled calls for the same surface can short-circuit. Settings
// records the cachedResult (so other surfaces benefit) but does NOT
// mark its surface as queried — the user's next "Check for updates"
// tap must always hit the backend.
export function rememberQuery(
  state: VersionCheckThrottleState,
  surface: VersionCheckSurface,
  result: VersionCheckResult,
): void {
  state.cachedResult = result;
  if (surface === 'startup' || surface === 'resume') {
    state.queriedSurfaces.add(surface);
  }
}

// PWA backend self-disable predicate. Same shape as the web branch of
// supportsUpdateCheckingFor (no Capacitor short-circuit because this
// is only consulted inside the PWA backend, which is never selected
// on Capacitor anyway). Belt-and-braces guard for any caller that
// bypassed supportsUpdateChecking and reached the backend directly —
// e.g. a future test fixture or a debug-console invocation. Mirrors
// the SW-skip condition in `client/src/index.html` (no
// `navigator.serviceWorker` OR `location.hostname.startsWith('local')`).
export function webBackendDisabled(flags: { hasServiceWorker: boolean; hostname: string }): boolean {
  if (!flags.hasServiceWorker) return true;
  if (flags.hostname.startsWith('local')) return true;
  return false;
}

// Numeric values from `@capawesome/capacitor-app-update`'s enums.
// Pinning them here lets the pure mapping helpers below stay free of
// plugin imports — the plugin pulls in `@capacitor/core` which the
// test runner can't load.
//
// From `AppUpdateAvailability`:
export const ANDROID_UPDATE_AVAILABLE = 2;
export const ANDROID_UPDATE_IN_PROGRESS = 3;
// From `FlexibleUpdateInstallStatus` (note: DOWNLOADED is 11, not the
// position you'd guess — the plugin mirrors Play's native constants):
export const ANDROID_INSTALL_PENDING = 1;
export const ANDROID_INSTALL_DOWNLOADING = 2;
export const ANDROID_INSTALL_INSTALLING = 3;
export const ANDROID_INSTALL_DOWNLOADED = 11;
// From `AppUpdateResultCode` (what startFlexibleUpdate() resolves with):
export const ANDROID_START_RESULT_NOT_ALLOWED = 4;

// Minimal structural type for the subset of the Capacitor App-Update
// plugin's `AppUpdateInfo` the Android backend cares about. Kept
// local so the pure helper can be tested without importing the
// plugin (and transitively `@capacitor/core`).
export interface AndroidUpdateInfo {
  updateAvailability: number;
  availableVersionCode?: string;
}

// Pure mapping from the Play plugin's update-info shape to our
// VersionCheckResult. Treats both UPDATE_AVAILABLE and
// UPDATE_IN_PROGRESS as "there is an update to surface": while a
// flexible update is downloading, Play reports UPDATE_IN_PROGRESS (not
// UPDATE_AVAILABLE), and the card must stay visible in its
// downloading/restart mode rather than vanishing on the next resume.
// The mode itself comes from mapAndroidInstallState below.
//
// A non-empty version code becomes availableVersion; the literal
// 'pending' fallback for a missing code matches the PWA backend's
// fallback semantics (a real update exists but Play didn't supply a
// version label — the prompt still renders).
export function mapAndroidPluginInfo(info: AndroidUpdateInfo): VersionCheckResult {
  const hasUpdate = info.updateAvailability === ANDROID_UPDATE_AVAILABLE
    || info.updateAvailability === ANDROID_UPDATE_IN_PROGRESS;
  if (!hasUpdate) {
    return { updateAvailable: false };
  }
  const version = info.availableVersionCode && info.availableVersionCode.length > 0
    ? info.availableVersionCode
    : 'pending';
  return { updateAvailable: true, availableVersion: version };
}

// Play's `InstallErrorCode` values that describe the PHONE, not our code
// (#6124). When getAppUpdateInfo() fails with one of these there is
// nothing the app can repair and nobody needs to act:
// -6 ERROR_INSTALL_NOT_ALLOWED: the device state forbids an update right
//    now (Google's examples: low battery, low disk space).
// -10 ERROR_APP_NOT_OWNED: the app was not acquired from Play (a sideload
//    or one of our dev builds), so Play will never offer it an update.
export const ANDROID_INSTALL_ERROR_NOT_ALLOWED = -6;
export const ANDROID_INSTALL_ERROR_APP_NOT_OWNED = -10;

// The plugin rejects getAppUpdateInfo() with Play's message string only,
// no code field. Play's InstallException message always starts with the
// code, as `<code>: Install Error(<code>): …`, so read it back from
// there. Only a leading `<integer>:` counts; any other shape (including a
// non-string) returns null, so an unfamiliar message is never mistaken
// for an expected one.
export function parseAndroidInstallErrorCode(message: unknown): number | null {
  if (typeof message !== 'string') return null;
  const match = /^(-?\d+):/.exec(message);
  return match ? Number(match[1]) : null;
}

// True only for the codes above. The caller reports these at the info
// tier; every other code, and a message with no code, stays a problem.
export function isExpectedAndroidInstallError(code: number | null): boolean {
  return code === ANDROID_INSTALL_ERROR_NOT_ALLOWED || code === ANDROID_INSTALL_ERROR_APP_NOT_OWNED;
}

// Pure mapping from the Play plugin's install-status (and the
// in-progress availability) to our three-state card model. DOWNLOADED
// → Ready (waiting for the user to tap Restart); the active-download
// statuses → Downloading; an UPDATE_IN_PROGRESS availability with no
// recognized install status still counts as Downloading so the card
// doesn't flip back to the actionable "Update" button mid-flight;
// everything else → Idle.
export function mapAndroidInstallState(
  updateAvailability: number,
  installStatus: number | undefined,
): UpdateInstallState {
  if (installStatus === ANDROID_INSTALL_DOWNLOADED) return UpdateInstallState.Ready;
  if (
    installStatus === ANDROID_INSTALL_PENDING
    || installStatus === ANDROID_INSTALL_DOWNLOADING
    || installStatus === ANDROID_INSTALL_INSTALLING
  ) {
    return UpdateInstallState.Downloading;
  }
  if (updateAvailability === ANDROID_UPDATE_IN_PROGRESS) return UpdateInstallState.Downloading;
  return UpdateInstallState.Idle;
}

// What an Update tap does on Android, decided from the install state the
// fresh getStatusAsync() just read (CYBERNETIC-FITNESS-APP-46D). Play
// answers NOT_AVAILABLE to startFlexibleUpdate() for anything but a
// plain UPDATE_AVAILABLE, so starting the flow again while a download is
// running, or finished and waiting for a restart, can only fail. The
// Settings row reaches this mid-download because the info mapping above
// keeps reporting an update while one is in progress; the home card can
// too, when its own mode lags behind Play.
export enum AndroidUpdateTapAction {
  // nothing is in flight: open Play's update sheet
  StartFlow = 'StartFlow',
  // a download is running: say so, do not call Play
  ShowDownloading = 'ShowDownloading',
  // the download finished: restart to install it, as the Restart button does
  CompleteUpdate = 'CompleteUpdate',
}

export function chooseAndroidUpdateTapAction(installState: UpdateInstallState): AndroidUpdateTapAction {
  switch (installState) {
    case UpdateInstallState.Downloading:
      return AndroidUpdateTapAction.ShowDownloading;
    case UpdateInstallState.Ready:
      return AndroidUpdateTapAction.CompleteUpdate;
    case UpdateInstallState.Idle:
      return AndroidUpdateTapAction.StartFlow;
  }
}

// True when startFlexibleUpdate() answered NOT_ALLOWED and the fresh
// update info had already said the flexible flow is not allowed. That is
// Play describing the phone's state, like the install errors #6124 moved
// to the info tier, so the caller records it as info. An unknown
// `flexibleUpdateAllowed` (undefined) is not proof, so it stays a problem.
export function isExpectedAndroidStartRejection(
  code: number,
  flexibleUpdateAllowed: boolean | undefined,
): boolean {
  return code === ANDROID_START_RESULT_NOT_ALLOWED && flexibleUpdateAllowed === false;
}

// The version of the Android flexible download that is in flight. Its only
// reader is the app_update.completed telemetry label, so a wrong write
// silently files the event under another version. The rule lives in the three
// functions below rather than at their callers:
//   - first seen wins: a download noticed by a status read (one that began in
//     an earlier session) is recorded only while nothing is recorded yet, so a
//     later read mid-download (the user opens Settings) cannot retarget it.
//   - the one exception: a download the user explicitly started owns the
//     version, so it overwrites.
//   - the flow ending (Idle) clears it, so a later completed event cannot
//     inherit it. Ready keeps it: the Restart tap's telemetry needs it.
export interface InProgressVersionState {
  version: string | null;
}

export function makeInProgressVersionState(): InProgressVersionState {
  return { version: null };
}

// a status read saw a download running or finished
export function recordVersionSeenWhileDownloading(state: InProgressVersionState, version: string): void {
  if (state.version === null) {
    state.version = version;
  }
}

// the user accepted Play's flexible flow for this version
export function recordVersionUserStarted(state: InProgressVersionState, version: string): void {
  state.version = version;
}

// no download is in flight any more
export function clearInProgressVersion(state: InProgressVersionState): void {
  state.version = null;
}

// Parses the response payload from the SW's `getVersion` reply. The
// SW posts `{ version: <AppVersion string> }` over the MessageChannel
// port. Returns the version string on a well-formed reply, or `null`
// on any malformed shape (missing field, wrong type, plain string,
// undefined). Pure helper so the parse-vs-fallback decision is
// unit-testable without a real MessageChannel.
//
// The null vs string return is the load-bearing distinction: the call
// site treats null as a protocol-breakage event (telemetry +
// substitute the UI literal `'pending'`) and a non-null string as the
// SW's authoritative answer (no telemetry, even when the SW itself
// genuinely returned the literal `'pending'` as its own
// `__APP_VERSION__`-missing fallback). Without this split the two
// signals would share the same return value and the telemetry would
// false-fire on a well-formed `{ version: 'pending' }`.
export function parseVersionFromSwMessage(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null;
  const version = (data as { version?: unknown }).version;
  if (typeof version !== 'string' || version.length === 0) return null;
  return version;
}

// ---------------------------------------------------------------------------
// Minimum supported version (#6770). See design-docs/in-app-update-prompt.md,
// "Minimum supported version".
// ---------------------------------------------------------------------------

// How the running build compares with `config/MinimumSupportedVersion`.
export enum MinimumVersionStatus {
  // the key is absent, or an empty / whitespace-only string: nobody is nagged
  NotSet = 'NotSet',
  // the key holds something that is not a version: nobody is nagged, and it is reported
  Malformed = 'Malformed',
  // the running build is at or above the minimum (or is not a release build)
  Supported = 'Supported',
  // the running build is below the minimum
  BelowMinimum = 'BelowMinimum',
}

const VERSION_SHAPE = /^\d+\.\d+\.\d+$/;

// Decides whether the running build is below the configured minimum.
//
// An empty string reads as NotSet, not Malformed: Admin → Config creates a new
// String key with globalValue '', so the owner's first per-user test would
// otherwise report a problem from every other user on every app start.
//
// A running build that is not version-shaped (the bare-commit-hash build, see the
// CAUTION in shared/src/version-compare.ts) reads as Supported, because
// compareSemver would read it as 0.0.0 and nag a developer build.
export function decideMinimumVersionStatus(runningAppVersion: string, configValue: unknown): MinimumVersionStatus {
  if (configValue === undefined || configValue === null) return MinimumVersionStatus.NotSet;
  if (typeof configValue === 'string' && configValue.trim() === '') return MinimumVersionStatus.NotSet;
  if (typeof configValue !== 'string') return MinimumVersionStatus.Malformed;
  const minimum = trimAppVersion(configValue.trim());
  if (!VERSION_SHAPE.test(minimum)) return MinimumVersionStatus.Malformed;
  const running = trimAppVersion(runningAppVersion);
  if (!VERSION_SHAPE.test(running)) return MinimumVersionStatus.Supported;
  return compareSemver(running, minimum) < 0 ? MinimumVersionStatus.BelowMinimum : MinimumVersionStatus.Supported;
}

// A pure "report this at most once per process" flag. The service holds one, so
// a malformed config/MinimumSupportedVersion is reported once per app start
// rather than on every Home entry.
export interface ReportOnceState {
  reported: boolean;
}

export function makeReportOnceState(): ReportOnceState {
  return { reported: false };
}

// True the first time it is called for this state, false every time after.
export function claimReportOnce(state: ReportOnceState): boolean {
  if (state.reported) return false;
  state.reported = true;
  return true;
}

// What Home does about the "Please update" alert.
export enum RequiredPromptAction {
  // open the alert now
  Open = 'Open',
  // do not open it in this process: the build is supported, no update exists, or
  // an Android download is already running or finished
  Skip = 'Skip',
  // it would open, but the user has left Home meanwhile: try again on the next
  // Home entry in this process
  RetryLater = 'RetryLater',
}

// The once-per-process flag behind the alert. Claimed synchronously before the
// first await, so two quick Home entries cannot both open the alert. Released only
// on RetryLater.
export interface RequiredPromptState {
  claimed: boolean;
}

export function makeRequiredPromptState(): RequiredPromptState {
  return { claimed: false };
}

export interface RequiredPromptInput {
  minimumStatus: MinimumVersionStatus;
  updateAvailable: boolean;
  installState: UpdateInstallState;
  homeIsActive: boolean;
}

// The alert decision once both answers are in. Releases the flag on RetryLater so
// the next Home entry asks again; every other answer leaves it claimed, so a
// supported or not-yet-updatable build is not re-checked on every Home entry.
export function decideRequiredPromptAction(
  state: RequiredPromptState,
  input: RequiredPromptInput,
): RequiredPromptAction {
  // the card's Downloading / Restart modes already cover a download in flight,
  // and starting Play's flow then can only fail (CYBERNETIC-FITNESS-APP-46D)
  const wouldOpen = input.minimumStatus === MinimumVersionStatus.BelowMinimum
    && input.updateAvailable
    && input.installState === UpdateInstallState.Idle;
  if (!wouldOpen) return RequiredPromptAction.Skip;
  if (!input.homeIsActive) {
    state.claimed = false;
    return RequiredPromptAction.RetryLater;
  }
  return RequiredPromptAction.Open;
}

export interface RequiredPromptDeps {
  getMinimumVersionStatusAsync(): Promise<MinimumVersionStatus>;
  getStatusAsync(): Promise<VersionCheckResult>;
  // resolves once the app's start-up navigation has settled. Home is entered before
  // boot's last navigation step, which sets the root again and would close a page
  // opened before it
  waitNavigationSettledAsync(): Promise<void>;
  installState(): UpdateInstallState;
  isHomeActive(): boolean;
  // opens the alert for the version the platform offers; called only on Open
  openAlert(availableVersion: string): void;
}

// The whole check Home runs on entry, in this order:
//   1. wait until the start-up navigation has settled. Boot's last navigation
//      step can replace the Home instance that started this check with a new
//      one, which runs its own check; so a Home that is no longer active stops
//      here WITHOUT taking the flag, or it would hold the flag while the new
//      Home skipped and then give it back with nobody left to use it;
//   2. claim the flag synchronously, before the next await; stop if already
//      claimed. Two Home entries racing past step 1 still open the alert once;
//   3. read the minimum status FIRST, and skip unless BelowMinimum, so a user
//      with no key set, or at or above the minimum, causes no store lookup;
//   4. only then ask the platform whether an update exists, and decide, reading
//      whether Home is still the active page again at that moment.
// Returns the action taken, so tests can see it.
export async function runRequiredPromptCheckAsync(
  state: RequiredPromptState,
  deps: RequiredPromptDeps,
): Promise<RequiredPromptAction> {
  await deps.waitNavigationSettledAsync();
  if (!deps.isHomeActive()) return RequiredPromptAction.RetryLater;
  if (state.claimed) return RequiredPromptAction.Skip;
  state.claimed = true;
  const minimumStatus = await deps.getMinimumVersionStatusAsync();
  if (minimumStatus !== MinimumVersionStatus.BelowMinimum) return RequiredPromptAction.Skip;
  const result = await deps.getStatusAsync();
  const action = decideRequiredPromptAction(state, {
    minimumStatus,
    updateAvailable: result.updateAvailable,
    installState: deps.installState(),
    homeIsActive: deps.isHomeActive(),
  });
  if (action === RequiredPromptAction.Open && result.updateAvailable) deps.openAlert(result.availableVersion);
  return action;
}

// Where the alert's Update button takes an Android user, once the install state
// is Idle. Play's own full-screen ("immediate") update screen restarts the app when
// finished; it is used only when Play says it is allowed. Otherwise the app opens
// the Play Store listing, where the user can update by hand (owner decision at STOP
// 1, 2026-10-23: the button must lead to the store). An unknown answer (undefined)
// is not permission.
export enum AndroidRequiredUpdateFlow {
  Immediate = 'Immediate',
  StoreListing = 'StoreListing',
}

export function chooseAndroidRequiredUpdateFlow(immediateUpdateAllowed: boolean | undefined): AndroidRequiredUpdateFlow {
  return immediateUpdateAllowed === true ? AndroidRequiredUpdateFlow.Immediate : AndroidRequiredUpdateFlow.StoreListing;
}

// E2E-only fake of the version check (#6770). While an E2E run is flagged AND this
// localStorage key holds `{ "availableVersion": "1.99.0", "minimumVersion": "v99.0.0" }`,
// VersionCheckService reports that update as available, the install state as Idle,
// and uses minimumVersion in place of the config value. Outside E2E runs the key
// is never read. See design-docs/in-app-update-prompt.md, "Minimum supported version".
export const E2E_FAKE_VERSION_CHECK_KEY = 'cs-e2e-fake-version-check';

export interface E2EFakeVersionCheck {
  availableVersion: string;
  minimumVersion: string;
}

// Parses the fake's stored JSON. Absent → null (no fake). Present but unusable →
// an error, so a test with a typo in its fixture fails loudly instead of silently
// running against the real check.
export function parseE2EFakeVersionCheck(raw: string | null): E2EFakeVersionCheck | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(`${E2E_FAKE_VERSION_CHECK_KEY} is not JSON`);
  }
  const fields = (parsed ?? {}) as { availableVersion?: unknown; minimumVersion?: unknown };
  if (typeof fields.availableVersion !== 'string' || fields.availableVersion === ''
    || typeof fields.minimumVersion !== 'string') {
    throw new Error(`${E2E_FAKE_VERSION_CHECK_KEY} needs string availableVersion and minimumVersion`);
  }
  return { availableVersion: fields.availableVersion, minimumVersion: fields.minimumVersion };
}
